import React, { useEffect, useState, useMemo } from "react";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import {
  formatDateTime12,
  getReportDateRange,
  type ReportDatePreset,
} from "@/utils/helpers";
import moment from "moment";
import { useGetData } from "@/hooks/useGetData";
import LoaderWrapper from "@/pages/LoaderWrapper";
import { patientV2LoaderWrapper } from "../shared/PatientV2CircularLoader";
import type { DateRange } from "react-day-picker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { CalendarDays } from "lucide-react";
import { isAfter, startOfDay } from "date-fns";
import ClientCardRowPaginatedTable from "@/components/patient-v2/MainContent/ClientCardRowPaginatedTable";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/use-auth";
import { useMarkReadingInvalid } from "./useMarkReadingInvalid";

const PRESET_OPTIONS: { value: ReportDatePreset; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7", label: "Last 7 Days" },
  { value: "last15", label: "Last 15 Days" },
  { value: "last30", label: "Last 30 Days" },
  { value: "last90", label: "Last 90 Days" },
  { value: "custom", label: "Custom date range" },
];

interface Reading {
  _id: string;
  readingDate: string;
  pulse?: number;
  status: string;
  isAbnormal: boolean;
  [key: string]: any;
}

interface PulseRateReadingsProps {
  patientId?: string;
  onDateRangeChange?: (range: { startDate: string; endDate: string }) => void;
  onReadingInvalidUpdated?: () => void;
}

const PulseRateReadings: React.FC<PulseRateReadingsProps> = ({
  patientId,
  onDateRangeChange,
  onReadingInvalidUpdated,
}) => {
  const { user } = useAuth();
  const userRole = useMemo(
    () => String((user as { role?: string } | null)?.role ?? ""),
    [user],
  );
  const isAdmin = userRole === "admin";
  const [datePreset, setDatePreset] = useState<ReportDatePreset>("last30");
  const [customRange, setCustomRange] = useState<DateRange | undefined>();
  const [draftCustomRange, setDraftCustomRange] = useState<DateRange | undefined>();
  const [customPickerOpen, setCustomPickerOpen] = useState(false);
  const [activeRangeBoundary, setActiveRangeBoundary] = useState<"from" | "to" | null>(null);

  const isFutureCalendarDay = (d: Date) =>
    isAfter(startOfDay(d), startOfDay(new Date()));

  const handleCustomRangeSelect = (_next: DateRange | undefined, day?: Date) => {
    if (!day || isFutureCalendarDay(day)) return;
    setDraftCustomRange((prev) => {
      const from = prev?.from;
      const to = prev?.to;

      if (!from || !to) {
        if (from && !to) {
          setActiveRangeBoundary(null);
          return day < from ? { from: day, to: from } : { from, to: day };
        }
        setActiveRangeBoundary("to");
        return { from: day, to: undefined };
      }

      if (day.toDateString() === from.toDateString()) {
        setActiveRangeBoundary("from");
        return prev;
      }
      if (day.toDateString() === to.toDateString()) {
        setActiveRangeBoundary("to");
        return prev;
      }

      if (activeRangeBoundary === "from") {
        setActiveRangeBoundary(null);
        return day <= to ? { from: day, to } : { from: to, to: day };
      }
      if (activeRangeBoundary === "to") {
        setActiveRangeBoundary(null);
        return day >= from ? { from, to: day } : { from: day, to: from };
      }

      setActiveRangeBoundary("to");
      return { from: day, to: undefined };
    });
  };

  // Fetch pulse rate readings
  const {
    apiData: readingsData,
    loading,
    err: error,
    fetchData,
  } = useGetData(
    `/reading/pulse`,
    { patientId },
    false,
    !!patientId,
    () => {}
  );

  const readings: Reading[] = readingsData?.data?.readings || readingsData?.data || [];

  const { moreActions: readingInvalidActions, confirmDialog: readingInvalidConfirmDialog } =
    useMarkReadingInvalid({
      enabled: isAdmin,
      onSuccess: () => {
        fetchData();
        onReadingInvalidUpdated?.();
      },
    });

  const { startDate, endDate } = useMemo(
    () =>
      getReportDateRange(
        datePreset,
        customRange?.from,
        customRange?.to ?? customRange?.from,
      ),
    [datePreset, customRange],
  );

  useEffect(() => {
    onDateRangeChange?.({ startDate, endDate });
  }, [startDate, endDate, onDateRangeChange]);

  const chartGranularity = useMemo<"day" | "month" | "year">(() => {
    if (datePreset === "today" || datePreset === "yesterday") return "day";
    if (datePreset === "last7" || datePreset === "last15" || datePreset === "last30")
      return "month";
    return "year";
  }, [datePreset]);

  // Filter readings based on selected date range
  const filteredReadings = useMemo(() => {
    if (!readings.length) return [];
    const rangeStart = moment(startDate, "YYYY-MM-DD").startOf("day");
    const rangeEnd = moment(endDate, "YYYY-MM-DD").endOf("day");
    return readings
      .filter((reading) => {
        const readingDate = moment(reading.readingDateLocal || reading.readingDate);
        return readingDate.isBetween(rangeStart, rangeEnd, undefined, "[]");
      })
      .sort((a, b) => {
        return moment(a.readingDateLocal || a.readingDate).valueOf() - moment(b.readingDateLocal || b.readingDate).valueOf();
      });
  }, [readings, startDate, endDate]);

  const tableReadings = useMemo(() => {
    return [...filteredReadings].sort((a, b) => {
      return moment(b.readingDateLocal || b.readingDate).valueOf() - moment(a.readingDateLocal || a.readingDate).valueOf();
    });
  }, [filteredReadings]);

  const pulseTableViewData = useMemo(
    () =>
      tableReadings.map((reading) => ({
        ...reading,
        date: formatDateTime12(reading.readingDateLocal || reading.readingDate),
        pulseReading: reading.pulse !== undefined ? `${reading.pulse} BPM` : "-",
      })),
    [tableReadings],
  );

  // Prepare chart data
  const chartData = useMemo(() => {
    return filteredReadings
      .filter((reading) => !reading.invalid)
      .map((reading) => ({
      date: moment(reading.readingDateLocal || reading.readingDate).format(
        chartGranularity === "day"
          ? "hh:mm A"
          : chartGranularity === "month"
          ? "MMM DD"
          : "MMM YYYY"
      ),
      pulse: reading.pulse || 0,
      fullDate: reading.readingDateLocal || reading.readingDate,
    }));
  }, [filteredReadings, chartGranularity]);

  const chartConfig = {
    pulse: {
      label: "Pulse Rate",
      color: "#3b82f6", // Blue
    },
  };

  return (
    <LoaderWrapper loading={loading} error={error} customLoaderComponent={patientV2LoaderWrapper()}>
      <div className="space-y-6">
        {readings.length === 0 ? (
          <>
            <div className="flex flex-wrap flex-col md:flex-row items-start gap-1.5 md:items-center justify-between">
              <h3 className="text-lg font-semibold text-tertiary">Pulse Rate Trend</h3>
            </div>
            <div className="py-8 text-center">
              <p className="text-sm text-tertiary">
                No pulse rate readings available.
              </p>
            </div>
          </>
        ) : (
          <>
            {/* Chart Section */}
            <div className="space-y-4">
              <div className="flex flex-wrap flex-col md:flex-row items-start gap-1.5 md:items-center justify-between">
                <h3 className="text-lg font-semibold text-tertiary">Pulse Rate Trend</h3>
                <div className="flex w-full md:w-fit items-center ml-auto justify-end gap-4">
                  <div className="flex items-center gap-2">
                    <Select
                      value={datePreset}
                      onValueChange={(v) => {
                        const preset = v as ReportDatePreset;
                        setDatePreset(preset);
                        if (preset !== "custom") {
                          setCustomPickerOpen(false);
                        } else {
                          setDraftCustomRange(customRange);
                          setActiveRangeBoundary(null);
                          setCustomPickerOpen(true);
                        }
                      }}
                    >
                      <SelectTrigger className="h-9 w-[min(200px,42vw)] shrink-0 rounded-lg border-gray-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRESET_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {datePreset === "custom" && (
                      <Popover
                        open={customPickerOpen}
                        onOpenChange={(open) => {
                          setCustomPickerOpen(open);
                          if (open) {
                            setDraftCustomRange(customRange);
                            setActiveRangeBoundary(null);
                            return;
                          }
                          setCustomRange(draftCustomRange);
                          setActiveRangeBoundary(null);
                        }}
                      >
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="h-9 shrink-0 rounded-lg whitespace-nowrap">
                            <CalendarDays className="mr-2 h-4 w-4" />
                            {draftCustomRange?.from && draftCustomRange?.to
                              ? `${draftCustomRange.from.toLocaleDateString()} — ${draftCustomRange.to.toLocaleDateString()}`
                              : draftCustomRange?.from
                                ? `${draftCustomRange.from.toLocaleDateString()} — …`
                                : "Pick dates"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="range"
                            numberOfMonths={2}
                            selected={draftCustomRange}
                            onSelect={handleCustomRangeSelect as any}
                            defaultMonth={draftCustomRange?.from}
                            disabled={(date) => isFutureCalendarDay(date)}
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                </div>
              </div>

              {chartData.length > 0 ? (
                <div className="border border-grey rounded-lg p-4 bg-white">
                  <ChartContainer config={chartConfig} className="h-[300px] w-full">
                    <LineChart
                      data={chartData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="date"
                        stroke="#6b7280"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        stroke="#6b7280"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                        label={{ value: "BPM", angle: -90, position: "insideLeft" }}
                      />
                      <ChartTooltip
                        content={
                          <ChartTooltipContent
                            labelFormatter={(value, payload) => {
                              if (payload && payload[0]) {
                                const data = payload[0].payload;
                                return moment(data.fullDate).format("MMM DD, YYYY hh:mm A");
                              }
                              return value;
                            }}
                          />
                        }
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="pulse"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        name="Pulse Rate"
                      />
                    </LineChart>
                  </ChartContainer>
                </div>
              ) : (
                <div className="border border-grey rounded-lg p-8 bg-white text-center">
                  <p className="text-sm text-tertiary">
                    No readings available for the selected time period.
                  </p>
                </div>
              )}
            </div>

            {/* Table Section */}
            {tableReadings.length > 0 && (
              <>
                <Separator />
                <ClientCardRowPaginatedTable
                  isIncludeSearch={false}
                  searchPlaceholder="Search readings..."
                  columnHeaders={[
                    { id: "date", label: "Date", isSortable: false },
                    {
                      id: "pulseReading",
                      label: "Pulse Rate (BPM)",
                      isSortable: false,
                    },
                  ]}
                  columnsData={tableReadings}
                  viewColumnData={pulseTableViewData}
                  loading={false}
                  moreActions={readingInvalidActions}
                />
              </>
            )}
            {readingInvalidConfirmDialog}
          </>
        )}
      </div>
    </LoaderWrapper>
  );
};

export default PulseRateReadings;

